<template>
  <div>
    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title class="q-body-2">
        <router-link class="text-grey-10 no-underline" :to="{ name: 'dashboard', params: { teamId: activeTeam.id } }">
          {{ $tc('imported_invoice', 2) }}
        </router-link> / <span class="text-weight-bold">{{ $t('capture_imported_invoice') }} <template v-if="total > 0">({{ total }})</template></span>
      </q-toolbar-title>
      <div v-if="!allCaptured">
        <q-btn
          :disabled="!hasPrevious"
          icon="navigate_before"
          class="q-mr-sm"
          flat
          dense
          @click="handlePrevious"
        />
        <q-btn
          :disabled="!hasNext"
          icon="navigate_next"
          class="q-mr-lg"
          flat
          dense
          @click="handleNext"
        />
        <q-btn
          color="primary"
          @click="handleSave"
        >
        {{ $t('save') }}
        </q-btn>
      </div>
    </q-toolbar>
    <div class="layout-padding" style="min-height: calc(100vh - 60px - 70px)">
      <document-upload
        :accept="['application/pdf']"
        :uploadFunction="uploadFunction"
        @uploaded="handleUploaded"
      />
      <div v-if="!allCaptured" class="row full-height q-mt-lg">
        <div class="col-12 col-md-6">
          <document-viewer v-if="document" :document="document" :showDownloadButton="true" />
        </div>
        <div class="col-12 col-md-6">
          <pending-invoice-import-form v-model="formData" :key="pendingInvoiceImport ? pendingInvoiceImport.id : '-1'" />
        </div>
      </div>
      <div v-else class="flex column text-center q-mt-lg">
        <img src="~assets/emptystates/items_empty.svg" />
        <h6 class="q-my-md text-faded">{{ $t('no_pending_invoice_import_to_capture') }}</h6>
      </div>
    </div>
  </div>
</template>

<script>
  import { isNil, omitBy } from 'lodash'
  import DocumentUpload from 'components/Common/DocumentUpload.vue'
  import DocumentViewer from 'components/Documents/DocumentViewer.vue'
  import { PendingInvoiceImportClient } from 'src/api/PendingInvoiceImport'
  import PendingInvoiceImportForm from 'components/Invoices/Import/PendingInvoiceImportForm.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'
  import { general } from 'src/config'
  import { mapGetters } from 'vuex'
  import { snakeCase } from 'change-case-object'

  export default {
    $_veeValidate: {
      validator: 'new'
    },
    components: {
      PendingInvoiceImportForm,
      DocumentViewer,
      DocumentUpload
    },
    data () {
      return {
        pendingInvoiceImport: null,
        meta: null,
        pendingInvoiceImportLoaded: false,
        formData: this.initialFormData()
      }
    },
    computed: {
      ...mapGetters(['activeTeam']),
      document () {
        if (this.pendingInvoiceImport === null) {
          return null
        }

        return this.pendingInvoiceImport.document.data
      },
      hasPrevious () {
        if (this.meta === null) {
          return false
        }

        return this.meta.pagination.current_page !== 1 &&
          this.meta.pagination.total_pages !== 1
      },
      hasNext () {
        if (this.meta === null) {
          return false
        }

        return this.meta.pagination.current_page !== this.meta.pagination.total_pages
      },
      allCaptured () {
        if (this.meta === null) {
          return false
        }

        return this.pendingInvoiceImportLoaded && this.meta.pagination.total === 0
      },
      total () {
        if (!this.meta) {
          return 0
        }

        return this.meta.pagination.total_pages * this.meta.pagination.count
      }
    },
    methods: {
      initialFormData () {
        return {
          invoiceTitle: this.$tc('invoice', 1),
          customer: null,
          invoiceAddresses: {
            invoiceAddresses: []
          },
          invoiceDate: null,
          deliveryDate: null,
          dueDate: null,
          invoiceNumber: null,
          totalNet: null,
          currency: general.base_currency,
          salesTaxAmount: null,
          salesTaxRate: null,
          projectId: null,
          status: 'paid'
        }
      },
      uploadFunction (file) {
        const formData = new FormData()
        formData.append('document', file.file)

        return new PendingInvoiceImportClient()
          .create(formData)
      },
      handleUploaded () {
        this.fetchCurrent()
      },
      resetFormData () {
        this.formData = this.initialFormData()
      },
      handlePrevious () {
        if (!this.hasPrevious) {
          return
        }

        this.fetchPrevious()
      },
      handleNext () {
        if (!this.hasNext) {
          return
        }

        this.fetchNext()
      },
      fetchCurrent () {
        return new PendingInvoiceImportClient()
          .page(this.meta.pagination.current_page)
          .limit(1)
          .filter([['complete', '!=', 'true']])
          .sort('created_at:asc')
          .include('document')
          .list()
          .then((response) => {
            const pagination = response.data.meta.pagination
            if (
              this.meta.pagination.current_page > pagination.total_pages &&
              pagination.total !== 0
            ) {
              this.meta = response.data.meta
              this.meta.pagination.current_page = pagination.total_pages
              return this.fetchCurrent()
            } else {
              this.handleResponse(response)
            }
          })
      },
      fetchPrevious () {
        return new PendingInvoiceImportClient()
          .page(this.meta.pagination.current_page - 1)
          .limit(1)
          .filter([['complete', '!=', 'true']])
          .sort('created_at:asc')
          .include('document')
          .list()
          .then(this.handleResponse)
      },
      fetchNext () {
        return new PendingInvoiceImportClient()
          .page(this.meta.pagination.current_page + 1)
          .limit(1)
          .filter([['complete', '!=', 'true']])
          .sort('created_at:asc')
          .include('document')
          .list()
          .then(this.handleResponse)
      },
      handleResponse (response) {
        const pendingInvoiceImport = response.data.data[0]
        const meta = response.data.meta
        this.pendingInvoiceImport = pendingInvoiceImport
        this.meta = meta
        this.pendingInvoiceImportLoaded = true
      },
      handleSave () {
        this.$validator.validateAll()
          .then(result => {
            if (result) {
              this.createInvoice()
            }
          })
      },
      createInvoice () {
        const dummyLineItem = [
          {
            type: 'table',
            children: [
              {
                type: 'group',
                heading: null,
                children: [
                  {
                    description: null,
                    discount_absolute: null,
                    discount_relative: null,
                    id: null,
                    item_name: 'Import Dummy Item',
                    item_number: null,
                    price: this.formData.totalNet,
                    quantity: 1,
                    sales_tax: this.formData.salesTaxRate
                  }
                ]
              }
            ]
          }
        ]

        const data = {
          ...snakeCase(this.formData),
          invoice_date: this.formData.invoiceDate
            ? date.formatDate(this.formData.invoiceDate, 'YYYY-MM-DD')
            : null,
          delivery_date: this.formData.deliveryDate
            ? date.formatDate(this.formData.deliveryDate, 'YYYY-MM-DD')
            : null,
          due_date: this.formData.dueDate
            ? date.formatDate(this.formData.dueDate, 'YYYY-MM-DD')
            : null,
          payment_date: this.formData.paymentDate
            ? date.formatDate(this.formData.paymentDate, 'YYYY-MM-DD')
            : null,
          'customer_id': this.formData.customer ? this.formData.customer.id : null,
          'invoice_address_id': this.formData.invoiceAddresses.invoiceAddress ? this.formData.invoiceAddresses.invoiceAddress.id : null,
          items: dummyLineItem
        }

        delete data.supplier
        delete data.sales_tax_rate
        delete data.sales_tax_amount

        const cleanedData = omitBy(data, isNil)

        new PendingInvoiceImportClient()
          .createInvoice(this.pendingInvoiceImport.id, cleanedData)
          .then(response => {
            this.$q.notify({ message: this.$t('invoice_imported_success'), type: 'positive' })
          })
          .then(() => {
            this.resetFormData()
          })
          .then(() => {
            return this.fetchCurrent()
          })
          .catch(createErrorFromResponse)
      }
    },
    beforeRouteEnter (to, from, next) {
      new PendingInvoiceImportClient()
        .limit(1)
        .filter([['complete', '!=', 'true']])
        .sort('created_at:asc')
        .include('document')
        .list()
        .then((response) =>
          next((vm) => vm.handleResponse(response))
        )
    },
    beforeRouteUpdate (to, from, next) {
      new PendingInvoiceImportClient()
        .limit(1)
        .filter([['complete', '!=', 'true']])
        .sort('created_at:asc')
        .include('document')
        .list()
        .then(this.handleResponse)
    },
    created () {
      this.formData.currency = general.base_currency
    }
  }
</script>
